import React, { useCallback, useEffect, memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash-es';
import { createSelector } from 'reselect';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { getHotSearch, getLenovoWords, cleanSearchRecommend } from '../actions/search';
import { setSearchKeyWord, setHotLenovoVisible } from '../actions/layout';
import styles from './Search.module.less';
import HotSearch from './HotSearch';
import LenovoWords from './LenovoWords';
import { CONTEXT } from '../config';
import { logPostInfo } from '../actions/log';
import { getQueryString } from '../utils/utils';

const selectHotSearch = createSelector(
  state => state.search,
  search => search.hotSearch.slice(0, 8),
);

const selectHotLenovoVisible = createSelector(
  state => state.layout,
  layout => layout.hotLenovoVisible,
);

const selectLenovoWords = createSelector(
  state => state.search,
  search => search.lenovoWords,
);

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectTopImgVideoVisible = createSelector(
  state => state.layout,
  layout => layout.topImgVideo,
);

export default memo(() => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const hotSearchData = useSelector(selectHotSearch);
  const hotLenovoVisible = useSelector(selectHotLenovoVisible);
  const lenovoWords = useSelector(selectLenovoWords);
  const logInfoData = useSelector(selectLogInfo);
  const topImgVideo = useSelector(selectTopImgVideoVisible); // 头部图片视频

  const [value, setValue] = useState('');
  const [changeIndex, setChangeIndex] = useState(null);

  // 搜索框下 hotLenovoVisible === 0 ? 热词 : 联想词
  const handleSearch = useCallback(() => {
    // search_type 1: 热词    2: 联想词   3: 搜索词
    dispatch(
      logPostInfo({
        source: 'Search',
        source_p: logInfoData.source,
        source_pp: logInfoData.source_p,
        search_word: value,
        search_type: 3,
      }),
    );
    dispatch(setHotLenovoVisible(-1));
    const keyword = getQueryString('keyword');

    if (!value.trim() || keyword === value) {
      return;
    }
    dispatch(cleanSearchRecommend());
    // 新搜索时页面滚动到顶部
    document.getElementById('container_content').scrollTo(0, 0);
    history.push(`${CONTEXT}/search/soft?keyword=${encodeURIComponent(value)}`);
  }, [dispatch, value, location.search]);

  const handleChange = useCallback(
    ev => {
      ev.persist();
      const inputValue = ev.target.value;
      setValue(inputValue);
      if (!inputValue.trim()) {
        dispatch(setHotLenovoVisible(0));
        dispatch(getLenovoWords(inputValue));
      } else {
        dispatch(setHotLenovoVisible(1));
        debounce(() => {
          setChangeIndex(null);
          dispatch(setSearchKeyWord(inputValue));
          dispatch(getLenovoWords(inputValue));
        }, 100)();
      }
    },
    [dispatch],
  );

  const handleArrowDownUp = (key, _changeIndex, _lenovoWords, _hotLenovoVisible) => {
    if (_lenovoWords.length > 0 && _hotLenovoVisible === 1) {
      if (key === 'ArrowDown') {
        if (_changeIndex === null) {
          setChangeIndex(0);
          setValue(_lenovoWords[0].keyWord);
        } else if (_changeIndex < _lenovoWords.length - 1) {
          setChangeIndex(_changeIndex + 1);
          setValue(_lenovoWords[_changeIndex + 1].keyWord);
        }
      }
      if (key === 'ArrowUp') {
        if (_changeIndex !== null && _changeIndex > 0) {
          setChangeIndex(_changeIndex - 1);
          setValue(_lenovoWords[_changeIndex - 1].keyWord);
        }
      }
    }
  };

  const handleKeyDown = useCallback(
    ev => {
      if (ev.key === 'Enter' && value) {
        handleSearch();
      }
      if ((ev.key === 'ArrowDown' || ev.key === 'ArrowUp') && value) {
        if (lenovoWords && lenovoWords.data && lenovoWords.data.length > 0) {
          handleArrowDownUp(ev.key, changeIndex, lenovoWords.data.slice(0, 6), hotLenovoVisible);
        }
      }
    },
    [value, changeIndex, lenovoWords, hotLenovoVisible],
  );

  const handleFocus = useCallback(
    ev => {
      if (!ev.target.value.trim()) {
        dispatch(setHotLenovoVisible(0));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (hotSearchData.length === 0) {
      dispatch(getHotSearch());
    }
  }, [dispatch]);

  useEffect(() => {
    setValue(getQueryString('keyword') || '');
  }, [location.search]);

  const handleClearKeyword = useCallback(() => {
    setValue('');
  }, [value]);

  return (
    <div
      className={
        topImgVideo && topImgVideo.visible && !topImgVideo.isScrolled
          ? styles.search_white
          : styles.search
      }
    >
      <input
        type="text"
        placeholder="搜索店内应用"
        className="input"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={value}
        onFocus={handleFocus}
      />

      {false && value && (
        <i
          className={classNames('icon iconfont icon-search_clear', styles.clear_keyword)}
          onClick={handleClearKeyword}
        />
      )}

      {<i className="icon iconfont icon-opt_search" onClick={handleSearch} />}

      {hotLenovoVisible === 0 && <HotSearch />}
      {hotLenovoVisible === 1 && <LenovoWords changeIndex={changeIndex} value={value} />}
    </div>
  );
});
